import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, type RefObject } from "react";
import type { AnvilTheme } from "@repo/blacksmith-ui/anvil/animations";
import { ANVIL_TRANSITION_MANIFEST, type AnvilTransitionVariant } from "@repo/blacksmith-ui/anvil/manifest";
import { useLatestRef } from "@/hooks/useLatestRef";
import { prefersReducedMotion } from "./scrollFollowMode";
import { FRAME_MS, decodeExitClips, nextOutro, prefetchOutros, revokeAll, withLoadTimeout } from "./useAnvilTransition";
const HANDOFF_DURATION_MS = 400;
const BESIDE_FADE_MS = 200;
const MIN_SEEN_MS = 100;
const MIN_OFFSET_PX = 2;
const MIN_SCALE_DELTA = 0.02;
const OVERLAY_Z_INDEX = "30"; // DESIGN.md page-overlay layer, below dialogs

type Rect = Pick<DOMRect, "left" | "top" | "width" | "height">;
type Handoff = {
  box: HTMLElement;
  rect: Rect;
  loop?: {
    name: AnvilTransitionVariant;
    startedAt: number;
  };
};
type Landing = {
  loop: AnvilTransitionVariant;
  adopt: (src: string) => void;
};
let pending: Handoff | null = null;
const listeners = new Set<() => void>();

// DESIGN.md's in-out repositioning curve, cubic-bezier(0.65, 0, 0.35, 1).
function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2;
}
const currentTheme = (): AnvilTheme => document.documentElement.classList.contains("dark") ? "dark" : "light";

// Skips the unused theme's image, hidden by CSS, which may never load.
function decodeImages(el: HTMLElement): Promise<unknown> {
  const images = el instanceof HTMLImageElement ? [el] : [...el.querySelectorAll("img")];
  return Promise.all(images.filter(image => getComputedStyle(image).display !== "none").map(image => image.decode()));
}

// Drops el's own placement (e.g. AnvilWorkingStatus's inline right and
// -translate-y-1/2) so the box alone places the copy.
function neutralCopy(el: HTMLElement): HTMLElement {
  // importNode clones like cloneNode, but is typed as el's type, not Node.
  const copy = document.importNode(el, true);
  Object.assign(copy.style, {
    position: "static",
    width: "100%",
    height: "100%",
    margin: "0",
    translate: "none",
    visibility: "visible",
    transition: "none"
  });
  return copy;
}
function clipImage(src: string): HTMLImageElement {
  const img = document.createElement("img");
  img.alt = "";
  img.draggable = false;
  img.src = src;
  Object.assign(img.style, {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    imageRendering: "pixelated"
  });
  return img;
}

// Lays the box out at `size` and scales it onto `rect`.
function place(box: HTMLElement, rect: Rect, size: Rect = rect) {
  Object.assign(box.style, {
    width: `${size.width}px`,
    height: `${size.height}px`,
    transform: `translate(${rect.left}px, ${rect.top}px) scale(${rect.width / size.width})`
  });
}

// Built and decoded while the loading Codey is on screen: a copy made as he
// unmounts paints blank until its frames decode.
function buildBox(el: HTMLElement, rect: Rect): HTMLElement {
  const box = document.createElement("div");
  box.dataset.anvilHandoff = "box";
  box.setAttribute("aria-hidden", "true");
  Object.assign(box.style, {
    position: "fixed",
    left: "0",
    top: "0",
    transformOrigin: "0 0",
    pointerEvents: "none",
    zIndex: OVERLAY_Z_INDEX,
    opacity: "0"
  });
  box.appendChild(neutralCopy(el));
  place(box, rect);
  document.body.appendChild(box);
  decodeImages(box).catch(() => {});
  return box;
}
function publish(handoff: Handoff) {
  pending?.box.remove();
  pending = handoff;
  // Only claimants in this commit or task may take it; a Codey that mounts
  // later is not the one the loading screen was handing off to. Unclaimed,
  // the box goes before it ever paints.
  requestAnimationFrame(() => {
    if (pending !== handoff) return;
    pending = null;
    handoff.box.remove();
  });
  for (const listener of [...listeners]) listener();
}
function intersects(a: Rect, b: Rect) {
  return a.left < b.left + b.width && b.left < a.left + a.width && a.top < b.top + b.height && b.top < a.top + a.height;
}

// e.g. the working Codey of a long session opened at a #msg- anchor sits below
// the transcript's scrollport; flying it would cross visible history.
function isVisibleInScrollports(el: HTMLElement, rect: Rect) {
  const viewport = {
    left: 0,
    top: 0,
    width: window.innerWidth,
    height: window.innerHeight
  };
  if (!intersects(rect, viewport)) return false;
  const clipping = (overflow: string) => overflow !== "" && overflow !== "visible";
  for (let ancestor = el.parentElement; ancestor; ancestor = ancestor.parentElement) {
    const {
      overflowX,
      overflowY
    } = getComputedStyle(ancestor);
    if ((clipping(overflowX) || clipping(overflowY)) && !intersects(rect, ancestor.getBoundingClientRect())) {
      return false;
    }
  }
  return true;
}
function takeHandoff(el: HTMLElement): {
  handoff: Handoff;
  to: Rect;
} | null {
  const handoff = pending;
  // A covered chat column is inert and transparent; flying a Codey that is
  // not yet visible would play unseen.
  if (!handoff || !el.isConnected || el.closest("[inert]")) return null;
  const to = el.getBoundingClientRect();
  if (to.width === 0 || to.height === 0) return null;
  if (!isVisibleInScrollports(el, to)) return null;
  pending = null;
  return {
    handoff,
    to
  };
}

// The box sets off at once and chases el's live rect, since an ancestor's
// layout animation or a settling scroll can still move el after the claim.
// If the clips decode before he lands, a looping loader plays his outro, then
// landing's intro, finishing in place if they outlast the flight. el shows
// only once its own frames decode; it would otherwise pop in blank. Without
// motion the box holds until then, and the returned release hands back the
// handoff, box intact, so a loader can forward it. `beside` stays hidden while
// he flies, then fades in as he lands.
function flyInto(el: HTMLElement, handoff: Handoff, to: Rect, landing?: Landing | null, beside?: HTMLElement | null): () => Handoff | null {
  const {
    box,
    rect: from,
    loop
  } = handoff;
  const moves = !prefersReducedMotion() && (Math.abs(from.left - to.left) >= MIN_OFFSET_PX || Math.abs(from.top - to.top) >= MIN_OFFSET_PX || Math.abs(from.width / to.width - 1) >= MIN_SCALE_DELTA);
  let phase: "holding" | "flying" | "done" = "holding";
  let frame = 0;
  let clips: Promise<string[]> | null = null;
  let adopted: string | null = null;
  el.style.visibility = "hidden";
  const stop = () => {
    cancelAnimationFrame(frame);
    el.style.removeProperty("visibility");
    if (moves && beside) {
      beside.style.transition = `opacity ${BESIDE_FADE_MS}ms ease-out`;
      beside.style.removeProperty("opacity");
    }
    void clips?.then(srcs => revokeAll(srcs.filter(src => src !== adopted)), () => {});
  };
  const finish = () => {
    if (phase === "done") return;
    phase = "done";
    stop();
    box.remove();
  };
  const release = (): Handoff | null => {
    if (phase !== "holding") {
      finish();
      return null;
    }
    phase = "done";
    stop();
    return handoff;
  };
  const images = withLoadTimeout(decodeImages(el));
  if (!moves) {
    void images.then(finish, finish);
    return release;
  }
  phase = "flying";
  // A fade left inline by an earlier landing would fade it out, not hide it.
  if (beside) Object.assign(beside.style, {
    transition: "none",
    opacity: "0"
  });
  const start = performance.now();
  // Each clip's last frame is the next one's first, so it plays a frame short.
  const introMs = landing ? ANVIL_TRANSITION_MANIFEST[landing.loop].introMs - FRAME_MS : 0;
  const flightMs = loop ? Math.max(HANDOFF_DURATION_MS, nextOutro(loop.name, start - loop.startedAt).outroMs - FRAME_MS + introMs) : HANDOFF_DURATION_MS;
  let shown = from;
  let size = from;
  let decoded = false;
  let script: {
    play: (now: number) => void;
    endsAt: number;
    loopSrc?: string;
  } | null = null;
  const show = (child: HTMLElement, layout: Rect) => {
    box.replaceChildren(child);
    size = layout;
    place(box, shown, size);
  };
  void images.then(() => {
    decoded = true;
    if (!loop && phase === "flying") show(neutralCopy(el), to);
  }, () => {
    decoded = true;
  });
  if (loop) {
    clips = decodeExitClips(loop.name, landing?.loop ?? null, currentTheme());
    void withLoadTimeout(clips).then(srcs => {
      const now = performance.now();
      if (phase !== "flying" || now - start >= flightMs) return;
      const exit = nextOutro(loop.name, now - loop.startedAt);
      const [nextSrc, loopSrc] = srcs.slice(landing ? -2 : -1);
      const outroAt = now + exit.exitInMs;
      const nextAt = outroAt + exit.outroMs - FRAME_MS;
      const img = clipImage(srcs[exit.outro]);
      let next: string | null = nextSrc;
      script = {
        play: at => {
          if (at < outroAt) return;
          if (img.parentNode !== box) show(img, from);
          if (!next || at < nextAt) return;
          img.src = next;
          next = null;
        },
        endsAt: nextAt + introMs,
        loopSrc
      };
      script.play(now);
    }, () => {});
  }
  const tick = () => {
    const now = performance.now();
    const live = el.getBoundingClientRect();
    if (live.width === 0) {
      finish();
      return;
    }
    script?.play(now);
    const t = Math.min(1, (now - start) / flightMs);
    if (t === 1 && decoded && (!script || now >= script.endsAt)) {
      if (landing && script?.loopSrc) {
        adopted = script.loopSrc;
        // Commits synchronously, so el shows the loop in this frame.
        landing.adopt(script.loopSrc);
      }
      finish();
      return;
    }
    const eased = easeInOutCubic(t);
    const lerp = (a: number, b: number) => a + (b - a) * eased;
    shown = {
      left: lerp(from.left, live.left),
      top: lerp(from.top, live.top),
      width: lerp(from.width, live.width),
      height: lerp(from.height, live.height)
    };
    place(box, shown, size);
    frame = requestAnimationFrame(tick);
  };
  tick();
  return release;
}

/**
 * The loading Codey: when it unmounts, the next Codey to mount (or become
 * visible) in the same commit flies in from where it stood, playing `loop`'s
 * outro on the way. Only a Codey seen (on screen for MIN_SEEN_MS, frames
 * decoded) hands off its own place. A loading screen replaced by another
 * before either is seen is a pass-through: the second holds what it claimed
 * (box showing, itself hidden) and forwards it if it goes unseen too, so the
 * flight starts where a Codey was last seen.
 */
export function useAnvilHandoffSource(ref, loop) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== loop || $[1] !== ref) {
    t0 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const claim = takeHandoff(el);
      if (claim) {
        el.style.visibility = "hidden";
      }
      let release = () => claim?.handoff ?? null;
      let own = null;
      let mounted = true;
      const becomeSeen = startedAt => {
        if (!mounted) {
          return;
        }
        const rect = el.getBoundingClientRect();
        if (rect.width === 0) {
          return;
        }
        own = {
          box: buildBox(el, rect),
          rect,
          loop: loop && {
            name: loop,
            startedAt
          }
        };
        if (loop) {
          prefetchOutros(loop, currentTheme());
        }
        if (!claim) {
          return;
        }
        const flight = flyInto(el, {
          ...claim.handoff,
          loop: undefined
        }, rect);
        release = () => flight() ? claim.handoff : null;
      };
      const decodedAt = decodeImages(el).then(_temp, _temp2);
      const seenTimer = setTimeout(() => void decodedAt.then(at => at !== null && becomeSeen(at)), MIN_SEEN_MS);
      return () => {
        mounted = false;
        clearTimeout(seenTimer);
        const inherited = release();
        if (claim) {
          el.style.removeProperty("visibility");
        }
        if (inherited) {
          own?.box.remove();
          publish(inherited);
          return;
        }
        if (!own) {
          return;
        }
        const rect_0 = el.getBoundingClientRect();
        if (rect_0.width > 0) {
          own.rect = rect_0;
          place(own.box, rect_0);
        }
        own.box.style.opacity = "1";
        publish(own);
      };
    };
    t1 = [ref, loop];
    $[0] = loop;
    $[1] = ref;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useLayoutEffect(t0, t1);
}

/**
 * A Codey that flies in from the loading Codey that just left. A looping
 * loader's outro plays on the way, then `landing`'s intro, whose loop goes to
 * `adopt` to start in step; without a landing it ends on the neutral pose.
 * `besideRef`'s text fades in as he lands.
 */
function _temp2() {
  return null;
}
function _temp() {
  return performance.now();
}
export function useAnvilHandoffTarget(ref, landing, besideRef) {
  const $ = _c(5);
  const landingRef = useLatestRef(landing);
  let t0;
  let t1;
  if ($[0] !== besideRef || $[1] !== landingRef || $[2] !== ref) {
    t0 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      let release = null;
      const claim = () => {
        if (el.style.visibility === "hidden") {
          return;
        }
        const taken = takeHandoff(el);
        if (!taken) {
          return;
        }
        release = flyInto(el, taken.handoff, taken.to, landingRef.current, besideRef?.current);
      };
      claim();
      const listener = () => queueMicrotask(claim);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        release?.()?.box.remove();
      };
    };
    t1 = [ref, landingRef, besideRef];
    $[0] = besideRef;
    $[1] = landingRef;
    $[2] = ref;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useLayoutEffect(t0, t1);
}